import { actionConstants, appConstants, NOT_ALLOWED_TO_CANCEL_BOL_STATUSES, resourceConstants } from "constants/index";
import { ActionCode, DeliveryInfo, DeliveryStatus, LogisticsDeliveryRequest, Shipment, ShipmentStatus } from "models";
import userUtils from "utils/user";
import { parseJsonObject } from "utils/independent";

const { permission, getPermissionCode } = userUtils;

const { STAFF_BFF } = appConstants;
const { DELIVERY_REQUEST } = resourceConstants;
const { PRINT_LABEL, READ, CANCEL, CREATE } = actionConstants;

const canPrintLabel = (delivery: DeliveryInfo, userPermissions: string[]): boolean => {
  const hasPermission = userPermissions.includes(
    getPermissionCode(permission(STAFF_BFF, DELIVERY_REQUEST, PRINT_LABEL))
  );
  return hasPermission && !!delivery.transportTrackingId;
};

const canViewDeliveryDetail = (userPermissions: string[]): boolean => {
  return userPermissions.includes(getPermissionCode(permission(STAFF_BFF, DELIVERY_REQUEST, READ)));
};

const canCancelRoute = (delivery: DeliveryInfo, userPermissions: string[], shipmenStatus: ShipmentStatus) => {
  const hasPermission = userPermissions.includes(getPermissionCode(permission(STAFF_BFF, DELIVERY_REQUEST, CANCEL)));
  return (
    hasPermission &&
    !!delivery.transportTrackingId &&
    delivery.status !== DeliveryStatus.cancelled &&
    !NOT_ALLOWED_TO_CANCEL_BOL_STATUSES.includes(shipmenStatus)
  );
};

const canCreateBol = (allowedUserActions: { actionCode: string }[] | undefined, userPermissions: string[]) => {
  const hasPermission = userPermissions.includes(getPermissionCode(permission(STAFF_BFF, DELIVERY_REQUEST, CREATE)));
  return (
    hasPermission && allowedUserActions?.some((allowedAction) => allowedAction.actionCode === ActionCode.createBoL)
  );
};

const getTransportTrackingIdAndTrackingUrl = (transportTrackingId: string) => {
  const { tracking_id = "", tracking_url = "" } = parseJsonObject(transportTrackingId);
  // Providers send either a JSON payload or the bare tracking id. Only the bare form can be used as
  // is, otherwise a payload carrying no tracking id (`"{}"` for an internal delivery team) would be
  // printed verbatim.
  const isJsonPayload = Boolean(transportTrackingId?.trim().startsWith("{"));

  return {
    trackingUrl: tracking_url,
    transportTrackingId: tracking_id || (isJsonPayload ? "" : transportTrackingId || ""),
  };
};

/**
 * Latest delivery request of a shipment. FFR creates request ids as `FFR-<shipmentId>-<suffix>` where the suffix
 * counts the delivery requests of the shipment (A, B, ..., Z, AA, ...), so a longer suffix, then a greater one, is newer.
 */
const getLatestDeliveryRequestOfShipment = (
  deliveryRequests: LogisticsDeliveryRequest[],
  shipmentId: Shipment["shipmentId"]
): LogisticsDeliveryRequest | undefined => {
  const requestIdPrefix = `FFR-${shipmentId}-`;

  return deliveryRequests
    .filter((deliveryRequest) => (deliveryRequest.requestId || "").startsWith(requestIdPrefix))
    .reduce<LogisticsDeliveryRequest | undefined>((latest, deliveryRequest) => {
      if (!latest) return deliveryRequest;
      if (deliveryRequest.requestId.length !== latest.requestId.length) {
        return deliveryRequest.requestId.length > latest.requestId.length ? deliveryRequest : latest;
      }
      return deliveryRequest.requestId > latest.requestId ? deliveryRequest : latest;
    }, undefined);
};

export default {
  canPrintLabel,
  canViewDeliveryDetail,
  canCancelRoute,
  canCreateBol,
  getTransportTrackingIdAndTrackingUrl,
  getLatestDeliveryRequestOfShipment,
};
